/* fail-safe */try{
/* Proactive Coaching — the cart, on the site itself.
 *
 * HISTORY, because this is the third cart architecture and the reasons matter:
 *
 *   1. Buy Button SDK (removed 2026-08-03): kept its OWN localStorage cart
 *      beside the real shop cart — the tab showed 30 items when the real cart
 *      held 1. Owner call: one cart. The floating cart, the slide-in drawer
 *      and the per-card quantity steppers all died with it.
 *   2. Plain GET /cart/add links (2026-08-03..05): one honest cart, but every
 *      Add navigated away to the shop origin, no count anywhere, no steppers.
 *   3. THIS: Shopify's Storefront Cart API. The cart is a server-side object
 *      owned by Shopify, created and read from THIS origin over CORS
 *      (verified: access-control-allow-origin * on the graphql.json endpoint).
 *      One cart, authoritative, readable — so the badge can never be the
 *      phantom "30" again. The iframe-bridge alternative is impossible:
 *      the shop serves x-frame-options DENY + frame-ancestors 'none'.
 *
 * TOKEN. Everything here is dormant until a Storefront API access token is
 * present (window.PCSFToken, set via Ghost Admin -> Code injection, or the
 * data-sf-token attribute on this script's tag). A storefront token is a
 * PUBLIC client-side credential by design — it ships to every visitor.
 * Dormant means: no badge, no steppers, no drawer, and every buy control
 * keeps its native href into the shop's GET /cart/add — exactly the
 * behaviour the site has today. No token, no change.
 *
 * TWO-CART EDGE. Items added on the shop origin itself (someone browsing
 * shop.proactivecoaching.ai directly) live in the shop's cookie cart, which
 * this cannot see. Every buy control ON THIS SITE goes through the API cart,
 * so the split only exists for direct shop-origin browsing — and the shop
 * header sends people back here. While ALIVE, cart icons are rewritten from
 * the shop cart page to the on-site drawer so the two are never shown side
 * by side; Checkout uses the API cart's own checkoutUrl.
 */
(function () {
    'use strict';

    var SHOP = 'https://shop.proactivecoaching.ai';
    var API = SHOP + '/api/2025-07/graphql.json';
    var KEY_ID = 'pc-cart:id';
    var KEY_COUNT = 'pc-cart:count';   // last verified count, for instant paint

    var tag = document.currentScript;
    var TOKEN = (window.PCSFToken || (tag && tag.getAttribute('data-sf-token')) || '').trim();
    if (!TOKEN) return;                // dormant — see header comment

    // ── GraphQL plumbing ────────────────────────────────────────────
    function gql(query, variables) {
        return fetch(API, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': TOKEN
            },
            body: JSON.stringify({ query: query, variables: variables || {} })
        }).then(function (r) {
            if (!r.ok) throw new Error('storefront api ' + r.status);
            return r.json();
        }).then(function (j) {
            if (j.errors && j.errors.length) throw new Error(j.errors[0].message);
            return j.data;
        });
    }

    var CART_FIELDS =
        'id totalQuantity checkoutUrl ' +
        'cost { subtotalAmount { amount currencyCode } } ' +
        'lines(first: 100) { nodes { id quantity merchandise { ... on ProductVariant { ' +
        'id title price { amount } product { title featuredImage { url } } image { url } } } } }';

    // ── Cart state ──────────────────────────────────────────────────
    var cart = null;          // last cart payload from the API
    var byVariant = {};       // numeric variant id -> { lineId, quantity }
    var pending = 0;          // in-flight mutations (steppers show busy)

    function numericId(gid) { return String(gid).split('/').pop(); }

    // ── Analytics ───────────────────────────────────────────────────
    // The cart shipped with NO measurement at all (2026-08-07 audit): every
    // add, every quantity change and every Checkout click was invisible, so
    // "which booklets do people actually try to buy" was unanswerable. These
    // use GA4's STANDARD ecommerce names so the built-in Monetisation reports
    // populate without any custom report building. Fail-safe: gtag may be
    // absent (blocked, or dormant token path) — never let it break a mutation.
    function ev(name, params) {
        try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); }
        catch (e) { /* analytics must never break the cart */ }
    }
    // Build a GA4 items[] entry from a variant id, reading the cart when the
    // line is present (post-mutation) and falling back to the id alone.
    function itemsFor(variantId, qty) {
        var node = null;
        if (cart && cart.lines) {
            cart.lines.nodes.forEach(function (l) {
                if (numericId(l.merchandise.id) === String(variantId)) node = l;
            });
        }
        var v = node && node.merchandise;
        return [{
            item_id: String(variantId),
            item_name: v ? v.product.title : 'unknown',
            item_variant: v && v.title !== 'Default Title' ? v.title : undefined,
            price: v ? parseFloat(v.price.amount) : undefined,
            quantity: Math.abs(qty || 1),
            item_category: 'booklet'
        }];
    }
    function cartItems() {
        if (!cart || !cart.lines) return [];
        return cart.lines.nodes.map(function (l) {
            var v = l.merchandise;
            return {
                item_id: numericId(v.id),
                item_name: v.product.title,
                price: parseFloat(v.price.amount),
                quantity: l.quantity,
                item_category: 'booklet'
            };
        });
    }

    function absorb(c) {
        cart = c;
        byVariant = {};
        if (c) c.lines.nodes.forEach(function (l) {
            byVariant[numericId(l.merchandise.id)] = { lineId: l.id, quantity: l.quantity };
        });
        try {
            if (c) { localStorage.setItem(KEY_ID, c.id); localStorage.setItem(KEY_COUNT, String(c.totalQuantity)); }
            else { localStorage.removeItem(KEY_ID); localStorage.setItem(KEY_COUNT, '0'); }
        } catch (e) { /* private mode */ }
        paintAll();
    }

    function fetchCart() {
        var id; try { id = localStorage.getItem(KEY_ID); } catch (e) { id = null; }
        if (!id) { absorb(null); return Promise.resolve(null); }
        return gql('query($id: ID!) { cart(id: $id) { ' + CART_FIELDS + ' } }', { id: id })
            .then(function (d) {
                // null = expired or checked out on another tab. Both mean: empty.
                absorb(d.cart);
                return d.cart;
            })
            .catch(function () { /* network blip — keep painting the cached state */ });
    }

    function createCart() {
        return gql('mutation { cartCreate { cart { ' + CART_FIELDS + ' } userErrors { message } } }')
            .then(function (d) {
                var c = d.cartCreate.cart;
                if (!c) throw new Error((d.cartCreate.userErrors[0] || {}).message || 'cartCreate failed');
                absorb(c);
                return c;
            });
    }

    function ensureCart() {
        if (cart) return Promise.resolve(cart);
        return fetchCart().then(function (c) { return c || createCart(); });
    }

    var ADD_MUTATION =
        'mutation($cartId: ID!, $lines: [CartLineInput!]!) { cartLinesAdd(cartId: $cartId, lines: $lines) { cart { ' +
        CART_FIELDS + ' } userErrors { field message } } }';

    function addTo(cartId, variantId, qty) {
        return gql(ADD_MUTATION, {
            cartId: cartId,
            lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: qty }]
        }).then(function (d) {
            var res = d.cartLinesAdd, errs = res.userErrors || [];
            if (res.cart) { absorb(res.cart); return; }
            var e = new Error((errs[0] || {}).message || 'add failed');
            /* Distinguish "this ITEM cannot be added" from "this CART is dead".
               Shopify answers an unsellable merchandise id with cart:null and a
               userError naming merchandiseId — the SAME shape as a dead cart.
               Telling them apart is the whole point: see the catch below. */
            e.itemRejected = errs.some(function (u) {
                return (u.field || []).indexOf('merchandiseId') > -1 ||
                       /merchandise/i.test(u.message || '');
            });
            throw e;
        });
    }

    function addLine(variantId, qty) {
        return ensureCart()
            .then(function (c) { return addTo(c.id, variantId, qty); })
            .catch(function (err) {
                /* THE ITEM was rejected — the cart is fine and still holds
                   everything. Do NOT touch it.

                   This branch exists because the old code discarded the cart on
                   ANY add failure. Proven live 2026-08-07: a cart holding two
                   booklets, plus one click on the $84.99 Complete Booklet
                   Collection (a Shopify BUNDLE, which the Buy Button channel
                   cannot carry, so its variant is invisible to this token), and
                   the shopper's basket was silently replaced with an empty one.
                   The cart survived on Shopify's side the whole time — we threw
                   it away ourselves. */
                if (err && err.itemRejected) {
                    /* Measured on purpose: an item the token cannot carry is
                       DEMAND WE ARE LOSING (the $84.99 Collection bundle is the
                       known case). Without this event that loss is invisible. */
                    ev('add_to_cart_failed', {
                        item_id: String(variantId), reason: 'item_rejected'
                    });
                    throw err;
                }

                /* Otherwise assume the cart id is dead (checked out in another
                   tab, or expired). That is the one case worth a fresh cart. */
                if (cart) { cart = null; try { localStorage.removeItem(KEY_ID); } catch (e) { /* private mode */ } }
                return createCart().then(function (c) { return addTo(c.id, variantId, qty); });
            })
            /* AFTER the catch, so it fires for the retry path too — and not at
               all when the add genuinely failed (a rethrow skips this). */
            .then(function () {
                ev('add_to_cart', { currency: 'USD', items: itemsFor(variantId, qty) });
            });
    }

    function setLineQty(variantId, qty) {
        var line = byVariant[variantId];
        if (!line) return qty > 0 ? addLine(variantId, qty) : Promise.resolve();
        var m = qty > 0
            ? ['cartLinesUpdate(cartId: $cartId, lines: [{ id: $lineId, quantity: $qty }])',
               'mutation($cartId: ID!, $lineId: ID!, $qty: Int!)']
            : ['cartLinesRemove(cartId: $cartId, lineIds: [$lineId])',
               'mutation($cartId: ID!, $lineId: ID!)'];
        var name = qty > 0 ? 'cartLinesUpdate' : 'cartLinesRemove';
        return gql(m[1] + ' { ' + m[0] + ' { cart { ' + CART_FIELDS + ' } userErrors { message } } }',
            qty > 0 ? { cartId: cart.id, lineId: line.lineId, qty: qty }
                    : { cartId: cart.id, lineId: line.lineId })
            .then(function (d) {
                /* Capture the item BEFORE absorb() overwrites the cart, or a
                   removal would have nothing left to describe. */
                var was = itemsFor(variantId, Math.abs(qty - (line.quantity || 0)) || 1);
                if (d[name].cart) absorb(d[name].cart);
                if (qty <= 0) ev('remove_from_cart', { currency: 'USD', items: was });
                else if (qty > line.quantity) ev('add_to_cart', { currency: 'USD', items: was });
                else ev('remove_from_cart', { currency: 'USD', items: was });
            });
    }

    // ── UI: badge ───────────────────────────────────────────────────
    // Count rule (Session C): show a number ONLY when it is the verified
    // API-cart count. The cached value is that same number from the previous
    // paint; it is reconciled by fetchCart() on every load and focus.
    function paintBadge() {
        var n = cart ? cart.totalQuantity : parseInt((function () { try { return localStorage.getItem(KEY_COUNT); } catch (e) { return '0'; } })(), 10) || 0;
        ['pc-tb-cart', 'pc-abar-cart'].forEach(function (cls) {
            [].forEach.call(document.getElementsByClassName(cls), function (el) {
                var b = el.querySelector('.pc-cart-badge');
                if (!n) { if (b) b.remove(); return; }
                if (!b) {
                    b = document.createElement('span');
                    b.className = 'pc-cart-badge';
                    b.setAttribute('aria-hidden', 'true');
                    el.appendChild(b);
                }
                b.textContent = n > 99 ? '99+' : String(n);
                var label = 'Cart, ' + n + (n === 1 ? ' item' : ' items');
                el.setAttribute('aria-label', label);
            });
        });
    }

    // While alive, cart icons open the on-site drawer instead of the shop's
    // cookie cart — the two must never be shown side by side.
    function captureCartLinks() {
        [].forEach.call(document.querySelectorAll('a[href$="shop.proactivecoaching.ai/cart"], a.pc-tb-pcart'), function (a) {
            if (a.__pcCart) return;
            a.__pcCart = true;
            a.addEventListener('click', function (e) {
                e.preventDefault();
                openDrawer();
            });
        });
    }

    // ── UI: card steppers ───────────────────────────────────────────
    // The counter the store used to have: once an item is in the cart, its
    // Add pill becomes a live [− n +]. Rendered from API-cart truth only.
    function paintSteppers() {
        [].forEach.call(document.querySelectorAll('[data-pc-add]'), function (btn) {
            var vid = btn.getAttribute('data-pc-add');
            var line = byVariant[vid];
            var qty = line ? line.quantity : 0;
            var has = btn.classList.contains('pcs-add--qty');
            if (!qty && has) {
                btn.classList.remove('pcs-add--qty');
                btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg><span>Add</span>';
            } else if (qty) {
                btn.classList.add('pcs-add--qty');
                btn.innerHTML =
                    '<span class="pcs-step" data-step="-1" role="button" tabindex="0" aria-label="Remove one">&minus;</span>' +
                    '<span class="pcs-qty" aria-label="Quantity in cart">' + qty + '</span>' +
                    '<span class="pcs-step" data-step="1" role="button" tabindex="0" aria-label="Add one more">+</span>';
            }
        });
    }

    // The floating cart tab — the SDK-era element rebuilt (the old
    // shopify-buy-frame--toggle, PC Blue, stuck to the screen edge,
    // site-wide). It EXISTS only while the cart has items: the moment the
    // count goes 0 -> 1 it slides out from the edge, which is the "pop"
    // that confirms an add alongside the toast. Clicking it opens the
    // drawer. CSS hides it while either drawer is open.
    function paintTab() {
        var host = document.getElementById('pc-cart-panel');
        if (!host) return;                 // template without the drawer: no tab
        var n = cart ? cart.totalQuantity : parseInt((function () { try { return localStorage.getItem(KEY_COUNT); } catch (e) { return '0'; } })(), 10) || 0;
        var tab = document.getElementById('pc-cart-tab');
        if (!n) { if (tab) tab.classList.remove('pc-open'); return; }
        if (!tab) {
            tab = document.createElement('button');
            tab.id = 'pc-cart-tab';
            tab.type = 'button';
            tab.innerHTML =
                '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>' +
                '<b class="pc-cart-tab-n"></b>';
            tab.addEventListener('click', openDrawer);
            document.body.appendChild(tab);
            // two frames so the first paint lands off-screen and the slide runs
            requestAnimationFrame(function () { requestAnimationFrame(function () { tab.classList.add('pc-open'); }); });
        } else {
            tab.classList.add('pc-open');
        }
        tab.querySelector('.pc-cart-tab-n').textContent = n > 99 ? '99+' : String(n);
        tab.setAttribute('aria-label', 'Open cart, ' + n + (n === 1 ? ' item' : ' items'));
    }

    function paintAll() { paintBadge(); paintSteppers(); paintDrawer(); paintTab(); captureCartLinks(); }

    // ── UI: toast ───────────────────────────────────────────────────
    var toastTimer = null;
    function toast(msg) {
        var t = document.getElementById('pc-cart-toast');
        if (!t) {
            t = document.createElement('div');
            t.id = 'pc-cart-toast';
            t.setAttribute('role', 'status');
            document.body.appendChild(t);
        }
        t.textContent = msg;
        t.classList.add('pc-show');
        clearTimeout(toastTimer);
        toastTimer = setTimeout(function () { t.classList.remove('pc-show'); }, 2600);
    }

    // ── UI: drawer ──────────────────────────────────────────────────
    function money(a) { return '$' + (Math.round(parseFloat(a) * 100) / 100).toFixed(2).replace(/\.00$/, ''); }

    function drawerEls() {
        var p = document.getElementById('pc-cart-panel');
        return p ? { panel: p, scrim: document.getElementById('pc-cart-scrim'), body: p.querySelector('.pcc-body'), foot: p.querySelector('.pcc-foot') } : null;
    }

    function paintDrawer() {
        var d = drawerEls();
        if (!d) return;
        var lines = cart ? cart.lines.nodes : [];
        if (!lines.length) {
            d.body.innerHTML = '<p class="pcc-empty">Your order is empty.<br>Everything on the store page adds straight in.</p>';
            d.foot.innerHTML = '';
            return;
        }
        d.body.innerHTML = lines.map(function (l) {
            var v = l.merchandise, vid = numericId(v.id);
            var title = v.product.title + (v.title && v.title !== 'Default Title' ? ' — ' + v.title : '');
            // Booklet VARIANTS carry no image of their own — fall back to the
            // product's featured image (this is every line in practice).
            var img = v.image || v.product.featuredImage;
            return '<div class="pcc-line" data-vid="' + vid + '">' +
                (img ? '<img src="' + img.url + (img.url.indexOf('?') > -1 ? '&' : '?') + 'width=120" alt="" loading="lazy">' : '<span class="pcc-noimg"></span>') +
                '<div class="pcc-line-in"><p class="pcc-t"></p>' +
                '<div class="pcc-line-row"><span class="pcc-stepper">' +
                '<button type="button" class="pcs-step" data-step="-1" aria-label="Remove one">&minus;</button>' +
                '<span class="pcs-qty">' + l.quantity + '</span>' +
                '<button type="button" class="pcs-step" data-step="1" aria-label="Add one more">+</button></span>' +
                '<span class="pcc-p">' + money(parseFloat(v.price.amount) * l.quantity) + '</span></div></div></div>';
        }).join('');
        // titles as text, not markup — product names are external data
        var ts = d.body.querySelectorAll('.pcc-t');
        lines.forEach(function (l, i) {
            var v = l.merchandise;
            ts[i].textContent = v.product.title + (v.title && v.title !== 'Default Title' ? ' — ' + v.title : '');
        });
        d.foot.innerHTML =
            '<div class="pcc-sub"><span>Subtotal</span><b>' + money(cart.cost.subtotalAmount.amount) + '</b></div>' +
            '<p class="pcc-note">Shipping and tax are worked out at checkout.</p>' +
            '<a class="pcc-checkout" href="' + cart.checkoutUrl + '">Checkout</a>' +
            '<button type="button" class="pcc-continue">Continue shopping</button>';
    }

    function openDrawer() {
        var d = drawerEls();
        if (!d) { location.href = SHOP + '/cart'; return; }   // no drawer on this template — go to the real thing
        ev('view_cart', {
            currency: 'USD',
            value: cart && cart.cost ? parseFloat(cart.cost.subtotalAmount.amount) : undefined,
            items: cartItems()
        });
        fetchCart();
        d.panel.classList.add('pc-open');
        d.scrim.classList.add('pc-open');
        d.panel.setAttribute('aria-hidden', 'false');
    }
    function closeDrawer() {
        var d = drawerEls();
        if (!d) return;
        d.panel.classList.remove('pc-open');
        d.scrim.classList.remove('pc-open');
        d.panel.setAttribute('aria-hidden', 'true');
    }

    // ── Wiring ──────────────────────────────────────────────────────
    document.addEventListener('click', function (e) {
        // 1. steppers — both on cards and inside the drawer
        var step = e.target.closest && e.target.closest('.pcs-step');
        if (step) {
            // stopImmediatePropagation, not stopPropagation: pc-store.js has
            // its OWN document-level [data-pc-add] listener that navigates to
            // the shop, and stopPropagation cannot stop another listener on
            // the SAME node. This one registers first (bundle order), so it
            // can cut the old one off. pc-store.js also stands down on its
            // own when window.PCCart exists - belt and braces.
            e.preventDefault(); e.stopImmediatePropagation();
            var host = step.closest('[data-pc-add]') || step.closest('.pcc-line');
            if (!host) return;
            var vid = host.getAttribute('data-pc-add') || host.getAttribute('data-vid');
            var cur = (byVariant[vid] || {}).quantity || 0;
            var next = cur + (step.getAttribute('data-step') === '1' ? 1 : -1);
            if (next < 0) next = 0;
            setLineQty(vid, next).catch(function () { toast('Could not update — try again'); });
            return;
        }
        // 2. quick-add pills on the store grids
        var btn = e.target.closest && e.target.closest('[data-pc-add]');
        if (btn && !btn.classList.contains('pcs-add--qty')) {
            e.preventDefault(); e.stopImmediatePropagation();
            addLine(btn.getAttribute('data-pc-add'), 1).then(function () {
                var label = (btn.getAttribute('aria-label') || '').replace(/^Add /, '').replace(/ to cart$/, '');
                toast((label || 'Added') + ' added to your order');
            }).catch(function () {
                // API down mid-session: honour the click the old way
                location.href = SHOP + '/cart/add?id=' + encodeURIComponent(btn.getAttribute('data-pc-add')) + '&quantity=1';
            });
            return;
        }
        // 3. BUY NOW anchors on the buy pages and the gift-card amounts —
        //    static GET /cart/add links; while alive they add in place and
        //    open the drawer (buy intent) instead of leaving the site.
        var a = e.target.closest && e.target.closest('a[href*="shop.proactivecoaching.ai/cart/add"]');
        if (a) {
            var m = a.href.match(/[?&]id=(\d+)/), q = a.href.match(/[?&]quantity=(\d+)/);
            if (!m) return;                        // unparseable — let it navigate
            e.preventDefault();
            addLine(m[1], q ? parseInt(q[1], 10) || 1 : 1)
                .then(function () { openDrawer(); })
                .catch(function () { location.href = a.href; });
            return;
        }
        // 4. Checkout — the last measurable moment on OUR origin.
        //    NOT named begin_checkout on purpose: the Shopify store carries the
        //    SAME measurement id (G-0NSNVEBQ51 via its Google channel tag), so
        //    Shopify already fires begin_checkout on the checkout page. Sending
        //    ours under that name would count one shopper twice and break the
        //    funnel. This measures the click on OUR side; compare it against
        //    Shopify's begin_checkout to see who drops at the domain handoff.
        if (e.target.closest && e.target.closest('.pcc-checkout')) {
            ev('cart_checkout_click', {
                currency: 'USD',
                value: cart && cart.cost ? parseFloat(cart.cost.subtotalAmount.amount) : undefined,
                items: cartItems()
            });
            return;                                   // let the link navigate
        }
        // 5. drawer chrome
        if (e.target.closest && e.target.closest('.pcc-continue')) { closeDrawer(); return; }
        if (e.target.id === 'pc-cart-scrim') { closeDrawer(); return; }
        if (e.target.closest && e.target.closest('#pc-cart-close')) { closeDrawer(); return; }
    });

    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var d = drawerEls();
        if (d && d.panel.classList.contains('pc-open')) closeDrawer();
    });

    // Re-sync whenever the page becomes visible again — catches a checkout
    // completed in another tab (cart query returns null -> badge clears).
    document.addEventListener('visibilitychange', function () {
        if (!document.hidden) fetchCart();
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) fetchCart(); });

    // /ask/'s cart icons land on the homepage with #cart to open the drawer.
    if (location.hash === '#cart') {
        setTimeout(openDrawer, 300);
        try { history.replaceState(null, '', location.pathname + location.search); } catch (e) {}
    }

    paintAll();     // instant paint from cache
    fetchCart();    // then verify against the API

    window.PCCart = { open: openDrawer, close: closeDrawer, refresh: fetchCart };
})();

}catch(e){/* cart is an enhancement — never halt the bundle */}
